import React, { useState, useEffect } from 'react';
import { ChartBarIcon, DocumentArrowDownIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { usersApi, billingApi } from '../services/api';
import { User, CreditHistory } from '../types';
import { UserCardShimmer, UsageStatsShimmer } from '../components/common/ShimmerEffect';
import UserTasks from '../components/users/UserTasks';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface UserUsageData {
  user: User;
  creditHistory: CreditHistory[];
  monthlyUsage: any[];
  totalCreditsUsed: number;
  currentBalance: number;
  tasksCount: {
    ide: number;
    web: number;
    devops: number;
    security: number;
  };
}

const UsageStats: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userUsageData, setUserUsageData] = useState<UserUsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState<'ide' | 'web' | 'devops' | 'security'>('ide');

  // Load all users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getUsers({ limit: 1000 });
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        setUsers(response.data.data);
      } else if (response.data?.items) {
        setUsers(response.data.items);
      } else if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserUsageData = async (user: User) => {
    try {
      setLoading(true);
      setSelectedUser(user);

      // Fetch credit history
      const creditHistoryResponse = await usersApi.getUserCreditHistory(user.user_id, 1000);
      const creditHistory = Array.isArray(creditHistoryResponse.data) 
        ? creditHistoryResponse.data 
        : creditHistoryResponse.data?.data || [];

      // Fetch monthly usage
      let monthlyUsage = [];
      try {
        const monthlyResponse = await billingApi.getUserMonthlyUsage(user.user_id, 12);
        monthlyUsage = monthlyResponse.data || [];
      } catch (error) {
        console.log('Monthly usage not available for user');
      }

      // Calculate tasks count from credit history by parsing app_type
      const tasksCounts = {
        ide: 0,
        web: 0,
        devops: 0,
        security: 0
      };

      // Parse credit history to extract app types and count tasks
      // Only count operations where description contains "User:" (not "Organization:")
      creditHistory.forEach((entry: CreditHistory) => {
        if (entry.operation_type === 'deduct' && entry.description && entry.description.includes('User:')) {
          const appType = extractAppTypeFromDescription(entry.description);
          if (appType && appType in tasksCounts) {
            tasksCounts[appType as keyof typeof tasksCounts]++;
          }
        }
      });

      // Calculate total credits used
      const totalCreditsUsed = creditHistory
        .filter((entry: CreditHistory) => entry.operation_type === 'deduct')
        .reduce((sum: number, entry: CreditHistory) => sum + Math.abs(entry.amount), 0);

      const usageData: UserUsageData = {
        user,
        creditHistory,
        monthlyUsage,
        totalCreditsUsed,
        currentBalance: user.remaining_credits || 0,
        tasksCount: tasksCounts
      };

      setUserUsageData(usageData);
    } catch (error) {
      console.error('Failed to load user usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePDFReport = async () => {
    if (!userUsageData) return;

    try {
      setGeneratingPDF(true);
      const { user, creditHistory, monthlyUsage, totalCreditsUsed, currentBalance, tasksCount } = userUsageData;

      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      
      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(40, 40, 40);
      pdf.text('User Usage Report', pageWidth / 2, 20, { align: 'center' });

      // User Info
      pdf.setFontSize(14);
      pdf.text('User Information', 20, 40);
      pdf.setFontSize(10);
      pdf.text(`Name: ${user.first_name || ''} ${user.last_name || ''}`, 20, 50);
      pdf.text(`Email: ${user.email}`, 20, 55);
      pdf.text(`Role: ${user.role}`, 20, 60);
      pdf.text(`Status: ${user.status}`, 20, 65);
      pdf.text(`Organizations: ${user.org_names_list?.join(', ') || 'None'}`, 20, 70);

      // Usage Summary
      pdf.setFontSize(14);
      pdf.text('Usage Summary', 20, 85);
      pdf.setFontSize(10);
      pdf.text(`Current Credit Balance: ${currentBalance.toLocaleString()}`, 20, 95);
      pdf.text(`Total Credits Used: ${totalCreditsUsed.toLocaleString()}`, 20, 100);
      pdf.text(`Credit Limit: ${user.credit_limit?.toLocaleString() || 'Unlimited'}`, 20, 105);
      pdf.text(`IDE Tasks: ${tasksCount.ide.toLocaleString()}`, 20, 110);
      pdf.text(`Web Tasks: ${tasksCount.web.toLocaleString()}`, 20, 115);
      pdf.text(`DevOps Tasks: ${tasksCount.devops.toLocaleString()}`, 20, 120);
      pdf.text(`Security Tasks: ${tasksCount.security.toLocaleString()}`, 20, 125);
      pdf.text(`Total Tasks: ${(tasksCount.ide + tasksCount.web + tasksCount.devops + tasksCount.security).toLocaleString()}`, 20, 130);

      // Monthly Usage Table
      if (monthlyUsage.length > 0) {
        pdf.setFontSize(14);
        pdf.text('Monthly Usage History', 20, 135);

        const enhancedMonthlyUsage = getEnhancedMonthlyUsage(monthlyUsage, creditHistory);
        const monthlyTableData = enhancedMonthlyUsage.map(month => [
          month.month_year || 'N/A',
          (month.credits_used || 0).toLocaleString(),
          (month.ide_tasks || 0).toLocaleString(),
          (month.web_tasks || 0).toLocaleString(),
          (month.devops_tasks || 0).toLocaleString(),
          (month.security_tasks || 0).toLocaleString()
        ]);

        autoTable(pdf, {
          startY: 145,
          head: [['Month', 'Credits Used', 'IDE Tasks', 'Web Tasks', 'DevOps Tasks', 'Security Tasks']],
          body: monthlyTableData,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246] },
          styles: { fontSize: 7 }
        });
      }

      // Credit History Table
      if (creditHistory.length > 0) {
        const finalY = (pdf as any).lastAutoTable?.finalY || 180;
        
        pdf.setFontSize(14);
        pdf.text('Recent Credit History', 20, finalY + 15);

        const historyTableData = creditHistory
          .filter(entry => !entry.description || entry.description.includes('User:'))
          .slice(0, 20).map(entry => [
          new Date(entry.created_at).toLocaleDateString(),
          entry.operation_type,
          entry.amount.toLocaleString(),
          entry.balance_after.toLocaleString(),
          entry.description.substring(0, 50) + (entry.description.length > 50 ? '...' : '')
        ]);

        autoTable(pdf, {
          startY: finalY + 20,
          head: [['Date', 'Type', 'Amount', 'Balance After', 'Description']],
          body: historyTableData,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246] },
          styles: { fontSize: 8 }
        });
      }

      // Footer
      const pageCount = (pdf as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pdf.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      // Save the PDF
      const fileName = `usage-report-${user.email.replace('@', '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const handleTasksClick = (taskType: 'ide' | 'web' | 'devops' | 'security') => {
    setSelectedTaskType(taskType);
    setShowTasksModal(true);
  };

  // Helper function to extract app type from credit history description
  const extractAppTypeFromDescription = (description: string): string | null => {
    if (!description) return null;
    
    // Look for patterns like "App usage: ana_ide.api_request" or "App usage: ide.code_completion"
    if (description.includes("App usage:")) {
      try {
        // Extract the part after "App usage: "
        const appPart = description.split("App usage: ")[1].split(".")[0];
        
        // Handle both "ana_ide" and "ide" formats
        let appType = appPart.startsWith("ana_") ? appPart.substring(4) : appPart;
        
        // Map to standard app types
        const appTypeMapping: { [key: string]: string } = {
          "ide": "ide",
          "security": "security", 
          "devops": "devops",
          "web": "web",
          "devops_app": "devops"
        };
        
        return appTypeMapping[appType] || null;
      } catch (error) {
        // If parsing fails, fall back to keyword matching
      }
    }
    
    // Check for other patterns in description
    const descriptionLower = description.toLowerCase();
    if (descriptionLower.includes("security")) return "security";
    if (descriptionLower.includes("devops")) return "devops";
    if (descriptionLower.includes("ide")) return "ide";
    if (descriptionLower.includes("web")) return "web";
    
    return null;
  };

  // Helper function to fetch tasks from credit history for DevOps and Security
  const fetchTasksFromCreditHistory = async (userId: number, appType: 'devops' | 'security', params: any) => {
    try {
      // Get credit history for the user
      const creditHistoryResponse = await usersApi.getUserCreditHistory(userId, 1000);
      const creditHistory = Array.isArray(creditHistoryResponse.data) 
        ? creditHistoryResponse.data 
        : creditHistoryResponse.data?.data || [];

      // Filter credit history for the specific app type
      // Only include operations where description contains "User:" (not "Organization:")
      const filteredTasks = creditHistory
        .filter((entry: CreditHistory) => {
          if (entry.operation_type !== 'deduct') return false;
          if (!entry.description || !entry.description.includes('User:')) return false;
          const entryAppType = extractAppTypeFromDescription(entry.description);
          return entryAppType === appType;
        })
        .map((entry: CreditHistory, index: number) => {
          // Convert credit history entry to task-like object
          const taskName = appType === 'devops' ? 'DevOps Operation' : 'Security Scan';
          
          // Try to extract more specific info from description
          let operationType = taskName;
          let target = entry.description;
          
          if (entry.description.includes('App usage:')) {
            const parts = entry.description.split('App usage: ')[1];
            if (parts) {
              const [appPart, ...descParts] = parts.split(' ');
              operationType = appPart.split('.')[1] || taskName;
              target = descParts.join(' ') || entry.description;
            }
          }

          return {
            id: entry.id || `${appType}_${index}`,
            operation_type: operationType,
            task_name: operationType,
            description: target,
            target: target,
            scan_type: operationType,
            credits_used: Math.abs(entry.amount),
            total_cost: Math.abs(entry.amount),
            created_at: entry.created_at,
            timestamp: entry.created_at,
            reference_id: entry.reference_id
          };
        });

      // Apply search filter if provided
      let searchFiltered = filteredTasks;
      if (params.task_name) {
        const searchTerm = params.task_name.toLowerCase();
        searchFiltered = filteredTasks.filter((task: any) => 
          task.description.toLowerCase().includes(searchTerm) ||
          task.operation_type.toLowerCase().includes(searchTerm)
        );
      }

      // Apply pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const skip = (page - 1) * limit;
      const paginatedTasks = searchFiltered.slice(skip, skip + limit);

      // Return in the expected format
      return {
        success: true,
        data: paginatedTasks,
        meta: {
          total: searchFiltered.length,
          pages: Math.ceil(searchFiltered.length / limit),
          page: page,
          limit: limit
        }
      };
    } catch (error) {
      console.error('Error fetching tasks from credit history:', error);
      return {
        success: false,
        data: [],
        meta: {
          total: 0,
          pages: 0,
          page: 1,
          limit: params.limit || 10
        }
      };
    }
  };

  // Helper function to get enhanced monthly usage with task counts
  const getEnhancedMonthlyUsage = (monthlyUsage: any[], creditHistory: CreditHistory[]) => {
    return monthlyUsage.map(month => {
      // Count tasks by app type for this month
      const monthTaskCounts = {
        ide_tasks: 0,
        web_tasks: 0,
        devops_tasks: 0,
        security_tasks: 0
      };

      // Filter credit history for this month and count by app type
      // Only count operations where description contains "User:" (not "Organization:")
      creditHistory.forEach(entry => {
        if (entry.operation_type === 'deduct' && entry.created_at && entry.description && entry.description.includes('User:')) {
          const entryDate = new Date(entry.created_at);
          const entryMonth = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}`;
          
          if (entryMonth === month.month_year) {
            const appType = extractAppTypeFromDescription(entry.description);
            if (appType && appType in { ide: true, web: true, devops: true, security: true }) {
              monthTaskCounts[`${appType}_tasks` as keyof typeof monthTaskCounts]++;
            }
          }
        }
      });

      return {
        ...month,
        ...monthTaskCounts
      };
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <ChartBarIcon className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Usage Statistics
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          View detailed usage statistics for all users and generate PDF reports
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Selection Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Select User
            </h2>

            {/* Search */}
            <div className="relative mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-[#1F2937] bg-white dark:bg-black text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>

            {/* Users List */}
            <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
              {loading ? (
                <UserCardShimmer />
              ) : (
                filteredUsers.map((user) => (
                  <button
                    key={user.user_id}
                    onClick={() => loadUserUsageData(user)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedUser?.user_id === user.user_id
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
                        : 'bg-gray-50 dark:bg-[#1F2937] border-gray-200 dark:border-[#1F2937] hover:bg-gray-100 dark:hover:bg-[#1F2937]/80'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-500/10 flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-primary-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.first_name || user.last_name 
                            ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                            : user.email
                          }
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Usage Details Panel */}
        <div className="lg:col-span-2">
          {!selectedUser ? (
            <div className="bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] rounded-xl p-12 text-center">
              <ChartBarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Select a User
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Choose a user from the list to view their usage statistics
              </p>
            </div>
          ) : loading ? (
            <UsageStatsShimmer />
          ) : userUsageData ? (
            <div className="space-y-6">
              {/* User Info Card */}
              <div className="bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {userUsageData.user.first_name || userUsageData.user.last_name 
                      ? `${userUsageData.user.first_name || ''} ${userUsageData.user.last_name || ''}`.trim()
                      : userUsageData.user.email
                    }
                  </h2>
                  <button
                    onClick={generatePDFReport}
                    disabled={generatingPDF}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    {generatingPDF ? 'Generating...' : 'Download PDF'}
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="text-center p-4 bg-gray-50 dark:bg-[#1F2937] rounded-lg">
                    <p className="text-2xl font-bold text-primary-500">
                      {userUsageData.currentBalance.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Current Credits</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-[#1F2937] rounded-lg">
                    <p className="text-2xl font-bold text-red-500">
                      {userUsageData.totalCreditsUsed.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Credits Used</p>
                  </div>
                  <button
                    onClick={() => handleTasksClick('ide')}
                    className="text-center p-4 bg-gray-50 dark:bg-[#1F2937] rounded-lg hover:bg-gray-100 dark:hover:bg-[#1F2937]/80 transition-colors cursor-pointer"
                  >
                    <p className="text-2xl font-bold text-blue-500">
                      {userUsageData.tasksCount.ide.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">IDE Tasks</p>
                  </button>
                  <button
                    onClick={() => handleTasksClick('web')}
                    className="text-center p-4 bg-gray-50 dark:bg-[#1F2937] rounded-lg hover:bg-gray-100 dark:hover:bg-[#1F2937]/80 transition-colors cursor-pointer"
                  >
                    <p className="text-2xl font-bold text-green-500">
                      {userUsageData.tasksCount.web.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Web Tasks</p>
                  </button>
                  <button
                    onClick={() => handleTasksClick('devops')}
                    className="text-center p-4 bg-gray-50 dark:bg-[#1F2937] rounded-lg hover:bg-gray-100 dark:hover:bg-[#1F2937]/80 transition-colors cursor-pointer"
                  >
                    <p className="text-2xl font-bold text-orange-500">
                      {userUsageData.tasksCount.devops.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">DevOps Tasks</p>
                  </button>
                  <button
                    onClick={() => handleTasksClick('security')}
                    className="text-center p-4 bg-gray-50 dark:bg-[#1F2937] rounded-lg hover:bg-gray-100 dark:hover:bg-[#1F2937]/80 transition-colors cursor-pointer"
                  >
                    <p className="text-2xl font-bold text-purple-500">
                      {userUsageData.tasksCount.security.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Security Tasks</p>
                  </button>
                </div>
              </div>

              {/* Monthly Usage */}
              {userUsageData.monthlyUsage.length > 0 && (
                <div className="bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Monthly Usage History
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <th className="px-4 py-2">Month</th>
                          <th className="px-4 py-2">Credits Used</th>
                          <th className="px-4 py-2">IDE Tasks</th>
                          <th className="px-4 py-2">Web Tasks</th>
                          <th className="px-4 py-2">DevOps Tasks</th>
                          <th className="px-4 py-2">Security Tasks</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-[#1F2937]">
                        {getEnhancedMonthlyUsage(userUsageData.monthlyUsage, userUsageData.creditHistory).map((month, index) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-[#1F2937]/10">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {month.month_year || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {(month.credits_used || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {(month.ide_tasks || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {(month.web_tasks || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {(month.devops_tasks || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {(month.security_tasks || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Credit History */}
              <div className="bg-white dark:bg-black border border-gray-200 dark:border-[#1F2937] rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Recent Credit History
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {userUsageData.creditHistory
                    .filter((entry) => !entry.description || entry.description.includes('User:'))
                    .slice(0, 50).map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#1F2937] rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-medium capitalize ${
                            entry.operation_type === 'deduct' 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {entry.operation_type}
                          </span>
                          <span className={`text-sm font-semibold ${
                            entry.operation_type === 'deduct' 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {entry.operation_type === 'deduct' ? '-' : '+'}
                            {Math.abs(entry.amount).toLocaleString()} credits
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                          {entry.description}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          Balance: {entry.balance_before.toLocaleString()} → {entry.balance_after.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(entry.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Tasks Modal */}
      {showTasksModal && selectedUser && (
        <UserTasks
          userId={selectedUser.user_id}
          onClose={() => setShowTasksModal(false)}
          fetchTasks={async (userId: number, params: any) => {
            // For DevOps and Security, use credit history data instead of tasks API
            if (params.task_type === 'devops' || params.task_type === 'security') {
              return await fetchTasksFromCreditHistory(userId, params.task_type, params);
            }
            
            // For IDE and Web, use the standard tasks API
            return await usersApi.fetchUserTasks(userId, params);
          }}
        />
      )}
    </div>
  );
};

export default UsageStats;
